<template>
  <div class="retention-message-page">
    <div class="page-top">
      <a class="back-link" @click="handleCancel">
        <i class="fas fa-chevron-left" /> Back to Retention Messages
      </a>
      <h2>{{ isNewMessage ? 'Create' : viewOnly ? 'View' : 'Edit' }} Retention Message</h2>
      <p v-if="!isNewMessage && message && message.name" class="page-name">{{ message.name }}</p>
    </div>

    <div class="layout">
      <div class="form-col">
        <div class="panel">
          <retention-message-form
            ref="form"
            :message="message"
            :view-only="viewOnly"
            :approved-mode="approvedMode"
            :is-new-message="isNewMessage"
            @save="handleSave"
            @cancel="handleCancel"
            @change="handleFormChange"
          />
          <div v-if="isSaving" class="saving-indicator">
            <span class="spinner-border spinner-border-sm"></span>
            Saving...
          </div>
        </div>
      </div>

      <div class="preview-col">
        <div class="preview-sticky">
          <span class="preview-label">Preview</span>
          <div class="phone-wrap">
            <div class="phone">
              <div class="phone-notch"></div>
              <div class="phone-screen">
                <div class="status-bar">
                  <span class="status-time">9:41</span>
                  <div class="status-icons">
                    <i class="fas fa-signal"></i>
                    <i class="fas fa-wifi"></i>
                    <i class="fas fa-battery-full"></i>
                  </div>
                </div>

                <div class="screen-body">
                  <div class="app-icon">
                    <div class="app-icon-grid"></div>
                  </div>

                  <h3 class="confirm-title">Confirm Cancellation</h3>
                  <p class="confirm-sub">
                    If you cancel this subscription, your service will end on January 15.
                  </p>

                  <div class="dev-card">
                    <div class="dev-card-label">FROM THE DEVELOPER</div>
                    <div class="dev-card-content">
                      <!-- Hero image when header is below the image (default ABOVE_BODY). -->
                      <div
                        v-if="hasMainImage && previewImageUrl && !headerAboveImage"
                        class="dev-card-image"
                      >
                        <img :src="previewImageUrl" :alt="previewMessage.altText || 'preview'" />
                      </div>
                      <div
                        v-else-if="hasMainImage && !previewImageUrl && !headerAboveImage"
                        class="dev-card-image dev-card-image-empty"
                      >
                        <i class="fas fa-image"></i>
                        <span>Image will appear here</span>
                      </div>

                      <div class="dev-card-header">
                        {{ previewMessage.header || 'Message header' }}
                      </div>

                      <!-- Hero image when header is above the image (ABOVE_IMAGE). -->
                      <div
                        v-if="hasMainImage && previewImageUrl && headerAboveImage"
                        class="dev-card-image dev-card-image-below"
                      >
                        <img :src="previewImageUrl" :alt="previewMessage.altText || 'preview'" />
                      </div>
                      <div
                        v-else-if="hasMainImage && !previewImageUrl && headerAboveImage"
                        class="dev-card-image dev-card-image-below dev-card-image-empty"
                      >
                        <i class="fas fa-image"></i>
                        <span>Image will appear here</span>
                      </div>

                      <div class="dev-card-body">
                        {{ previewMessage.body || 'Message body will appear here.' }}
                      </div>

                      <!-- Bulleted list (below body, separated by Apple's hairline). -->
                      <template v-if="hasBullets">
                        <div class="dev-card-separator"></div>
                        <ul v-if="previewBullets.length > 0" class="dev-card-bullets">
                          <li v-for="(b, i) in previewBullets" :key="i">
                            <span class="bullet-icon">
                              <img v-if="b.imageUrl" :src="b.imageUrl" :alt="b.altText || 'bullet icon'" />
                              <i v-else class="fas fa-circle"></i>
                            </span>
                            <span class="bullet-text">{{ b.text || 'Bullet text' }}</span>
                          </li>
                        </ul>
                        <ul v-else class="dev-card-bullets dev-card-bullets-empty">
                          <li>
                            <span class="bullet-icon"><i class="fas fa-circle-notch"></i></span>
                            <span class="bullet-text">Bullets will appear here</span>
                          </li>
                        </ul>
                      </template>

                      <!-- Switch-plan: Apple renders an embedded product row with
                           a full-width Subscribe pill below it, separated from the body
                           by a hairline. -->
                    </div>
                  </div>
                </div>

                <div class="screen-actions">
                  <div class="screen-btn">Cancel Subscription</div>
                  <div class="screen-btn">Keep Subscription</div>
                </div>

                <div class="home-indicator"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RetentionMessageForm from '../components/Retention/MessageForm.vue';
import { permissionsMixin } from '../mixins/permissions.mixin';
import {
  MESSAGE_TYPES,
  TYPES_WITH_MAIN_IMAGE,
  TYPES_WITH_BULLETS,
  HEADER_POSITIONS
} from '../constants/retentionMessageTypes';

const VALID_QUERY_TYPES = new Set([
  MESSAGE_TYPES.TEXT,
  MESSAGE_TYPES.IMAGE,
  MESSAGE_TYPES.BULLETED,
  MESSAGE_TYPES.BULLETED_IMAGE
]);

export default {
  mixins: [permissionsMixin],
  components: {
    RetentionMessageForm
  },
  data() {
    return {
      isSaving: false,
      liveForm: null
    };
  },
  computed: {
    message() {
      const id = this.$route.params.id;
      if (id) {
        const msg = this.$store.getters.retentionMessageById(id);
        return msg || this.newMessage();
      }
      return this.newMessage();
    },
    previewMessage() {
      return this.liveForm
        ? { ...this.message, ...this.liveForm }
        : this.message;
    },
    viewOnly() {
      if (!this.canEditRetentionMessage) return true;
      const state = this.message?.messageState;
      return state !== 'draft' && state !== 'APPROVED';
    },
    approvedMode() {
      return this.message?.messageState === 'APPROVED' && this.canEditRetentionMessage;
    },
    isNewMessage() {
      return !this.$route.params.id;
    },
    hasMainImage() {
      return TYPES_WITH_MAIN_IMAGE.has(this.previewMessage.messageType);
    },
    hasBullets() {
      return TYPES_WITH_BULLETS.has(this.previewMessage.messageType);
    },
    headerAboveImage() {
      return this.previewMessage.headerPosition === HEADER_POSITIONS.ABOVE_IMAGE;
    },
    previewImageUrl() {
      const m = this.previewMessage;
      if (!this.hasMainImage || !m.imageIdentifier) return null;
      const images = this.$store.getters['retention/images'] || [];
      const img = images.find(i => i.imageIdentifier === m.imageIdentifier);
      return img?.imageUrl || null;
    },
    previewBullets() {
      const m = this.previewMessage;
      if (!this.hasBullets || !Array.isArray(m.bulletPoints)) return [];
      const images = this.$store.getters['retention/images'] || [];
      return m.bulletPoints.map((b) => ({
        ...b,
        imageUrl: b?.imageIdentifier
          ? (images.find(i => i.imageIdentifier === b.imageIdentifier)?.imageUrl || null)
          : null
      }));
    }
  },
  async mounted() {
    await this.$store.dispatch('retention/fetchImages');
  },
  methods: {
    newMessage() {
      const queryType = this.$route.query?.type;
      const messageType = VALID_QUERY_TYPES.has(queryType) ? queryType : MESSAGE_TYPES.TEXT;
      return {
        header: '',
        body: '',
        messageType,
        imageIdentifier: null,
        altText: '',
        headerPosition: HEADER_POSITIONS.ABOVE_BODY,
        bulletPoints: [],
        messageState: 'draft',
        createdAt: Date.now()
      };
    },
    handleFormChange(formData) {
      this.liveForm = formData;
    },
    async handleSave(payload) {
      this.isSaving = true;
      try {
        if (this.isNewMessage) {
          await this.$store.dispatch('retention/addMessage', payload);
        } else {
          await this.$store.dispatch('retention/editMessage', {
            id: this.$route.params.id,
            data: payload
          });
        }
        this.$router.push({
          name: 'RetentionMessagesTab',
          params: { aid: this.$store.getters.activeApp }
        });
      } catch (error) {
        console.error('Error saving message:', error);
        if (this.$refs.form) this.$refs.form.resetSaving();
      } finally {
        this.isSaving = false;
      }
    },
    handleCancel() {
      this.$router.push({
        name: 'RetentionMessagesTab',
        params: { aid: this.$store.getters.activeApp }
      });
    }
  }
};
</script>

<style scoped>
.retention-message-page {
  flex: 1;
  min-width: 0;
  padding: 0.25rem 0.25rem 2rem;
  color: rgba(255, 255, 255, 0.88);
}

.page-top {
  margin-bottom: 0.85rem;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  cursor: pointer;
  margin-bottom: 0.4rem;
  transition: color 0.12s ease;
}

.back-link:hover {
  color: #03B3DA;
}

.back-link i {
  font-size: 0.7rem;
}

.page-top h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #fff;
  font-weight: 600;
}

.page-name {
  margin: 0.2rem 0 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.55);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 1.25rem;
  align-items: center;
}

@media (max-width: 1100px) {
  .layout {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
}

.panel {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 1.25rem;
}

.preview-col {
  align-self: center;
}

.preview-sticky {
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.preview-label {
  display: block;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
}

.phone-wrap {
  display: flex;
  justify-content: center;
  width: 100%;
}

.phone {
  position: relative;
  width: 360px;
  height: 740px;
  background: #0a0a0a;
  border-radius: 50px;
  padding: 11px;
  box-shadow:
    0 0 0 2px #1a1a1a,
    0 0 0 4px #2a2a2a,
    0 30px 60px rgba(0, 0, 0, 0.6),
    0 12px 24px rgba(0, 0, 0, 0.5);
}

.phone-notch {
  position: absolute;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  width: 112px;
  height: 32px;
  background: #000;
  border-radius: 999px;
  z-index: 2;
}

.phone-screen {
  position: relative;
  width: 100%;
  height: 100%;
  background: #1c1c1e;
  border-radius: 36px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 28px 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif;
}

.status-time {
  z-index: 3;
}

.status-icons {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  z-index: 3;
}

.status-icons .fa-battery-full {
  font-size: 14px;
}

.screen-body {
  flex: 1;
  padding: 28px 22px 0;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif;
}

.app-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-icon-grid {
  width: 100%;
  height: 100%;
  border-radius: 14px;
  background:
    linear-gradient(rgba(255, 255, 255, 0.12) 1px, transparent 1px) 0 0 / 8px 8px,
    linear-gradient(90deg, rgba(255, 255, 255, 0.12) 1px, transparent 1px) 0 0 / 8px 8px,
    radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02));
  background-color: #2a2a2c;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.confirm-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  line-height: 1.15;
  letter-spacing: -0.3px;
}

.confirm-sub {
  margin: 0 0 16px;
  font-size: 14px;
  color: rgba(235, 235, 245, 0.6);
  line-height: 1.35;
}

.dev-card {
  background: rgba(118, 118, 128, 0.18);
  border-radius: 12px;
  overflow: hidden;
}

.dev-card-label {
  padding: 10px 14px 8px;
  font-size: 10px;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: rgba(235, 235, 245, 0.55);
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.06);
}

.dev-card-content {
  padding: 12px 14px 14px;
  /* Cap height so the on-phone scroll behaviour is faithfully mirrored: when
     the message exceeds the visible card area (e.g. 4–5 bullets, or a long
     body), it scrolls inside the card just like Apple's bottom sheet does on
     device. overflow-x: hidden keeps the image bleed (negative margins on
     .dev-card-image) clipped to the card edge — vertical-only scroll. */
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.28) transparent;
}

.dev-card-content::-webkit-scrollbar {
  width: 5px;
}

.dev-card-content::-webkit-scrollbar-track {
  background: transparent;
}

.dev-card-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.28);
  border-radius: 3px;
}

.dev-card-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.4);
}

/* Bleed the image to the dev-card edges (matches Apple's edge-to-edge render).
   `object-fit: contain` + no max-height means the rendered PNG appears at its
   true aspect ratio — including any white letterbox baked in by sharp at upload
   time. The preview is a faithful mirror of what Apple shows. */
.dev-card-image {
  margin: -12px -14px 12px;
  background: #2a2a2c;
  display: block;
  line-height: 0;
}

.dev-card-image-below {
  margin: 12px -14px 12px;
}

.dev-card-image img {
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
}

.dev-card-image-empty {
  height: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 11px;
  line-height: 1.2;
}

.dev-card-image-empty i {
  font-size: 22px;
}

.dev-card-header {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 4px;
  line-height: 1.3;
  word-wrap: break-word;
}

.dev-card-body {
  font-size: 13px;
  color: rgba(235, 235, 245, 0.85);
  line-height: 1.35;
  word-wrap: break-word;
}

/* Apple separates the body from the bullets / product card with a hairline. */
.dev-card-separator {
  margin: 10px -14px;
  height: 0.5px;
  background: rgba(255, 255, 255, 0.08);
}

.dev-card-bullets {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dev-card-bullets li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: rgba(235, 235, 245, 0.85);
  line-height: 1.3;
}

/* Apple renders bullet icons flush against the dev-card background — no pill,
   no rounded container. `object-fit: contain` so any baked-in white padding is
   visible (matches Apple's render, no client-side cropping). */
.bullet-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.45);
  font-size: 11px;
}

.bullet-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.bullet-text {
  flex: 1;
  word-wrap: break-word;
  overflow-wrap: break-word;
  min-width: 0;
}

.dev-card-bullets-empty li {
  color: rgba(235, 235, 245, 0.35);
  font-style: italic;
}

/* Switch-plan product card inside the dev card — mirrors Apple:
   icon + (name above price) row, with a full-width Subscribe pill below.
   Display name and price are italic placeholders since App Store fills them
   in at runtime — we don't have them on the panel side. */
.dev-card-product {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dev-card-product-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dev-card-product-icon {
  width: 32px;
  height: 32px;
  border-radius: 7px;
  overflow: hidden;
  flex-shrink: 0;
}

.dev-card-product-icon .app-icon-grid {
  width: 100%;
  height: 100%;
}

.dev-card-product-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dev-card-product-name {
  font-size: 13px;
  font-weight: 600;
  color: rgba(235, 235, 245, 0.6);
  font-style: italic;
  line-height: 1.25;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.dev-card-product-sub {
  font-size: 11px;
  color: rgba(235, 235, 245, 0.4);
  font-style: italic;
  line-height: 1.25;
  margin-top: 2px;
}

.dev-card-product-cta {
  display: block;
  width: 100%;
  text-align: center;
  background: rgba(0, 122, 255, 0.18);
  color: #4aa3ff;
  border-radius: 999px;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
}

.screen-actions {
  padding: 0 22px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif;
}

.screen-btn {
  background: rgba(118, 118, 128, 0.14);
  border: 0.5px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  padding: 12px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  word-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
}

.screen-btn-primary {
  background: #007aff;
  border-color: #007aff;
  color: #fff;
  font-weight: 600;
}

.home-indicator {
  position: absolute;
  bottom: 6px;
  left: 50%;
  transform: translateX(-50%);
  width: 110px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
}

.saving-indicator {
  margin-top: 0.75rem;
  color: #03B3DA;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}
</style>
